import {IconButton, Dialog, DialogTitle, DialogContent, useTheme, Box} from "@mui/material";
import {FiTrash2, FiMail, FiX} from "react-icons/fi";
import {useLocation, useNavigate} from "react-router-dom";
import {useState} from "react";
import ListView from "../components/ListView/ListView";
import Window from "../components/Window/Window";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import SearchBar from "../components/ui/SearchBar";
import ExportCsvButton from "../components/ui/ExportCsvButton";
import TicketForm from "../components/Tickets/TicketForm";
import {userColumns} from "../constants/dataGridColumns";
import {userSearchFields} from "../constants/searchFields";
import useApiRequest from "../hooks/useApiRequest";
import useConfirmDialog from "../hooks/useConfirmDialog";
import usePaginationSearch from "../hooks/usePaginationSearch";
import {fetchAllPages} from "../utils/pagination";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../hooks/useAuthPermissions";
import {snackbar} from "../utils/snackbar";

const Users = () => {
  const navigate = useNavigate();
  const {state} = useLocation();
  const theme = useTheme();
  const {list, remove} = useApiRequest({baseUrl: "/users", resource: "users"});
  const {can} = useAuthPermissions();

  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const {filters, applyFilters, resetFilters, refresh, dataGridProps, listState} = usePaginationSearch({
    fetcher: list,
    initialFilters: {},
    initialSortModel: [{field: "created_at", sort: "desc"}],
    initialListState: state?.listState
  });

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (id) => {
    const {success} = await remove(id);
    if (!success) return;

    snackbar.success("Usuario eliminado correctamente.");
    refresh();
  });

  const handleRowClick = (user) => {
    if (!user?.id) {
      snackbar.error("No se ha podido abrir el usuario seleccionado.");
      return;
    }
    navigate(`/user-form`, {state: {id: user?.id, from: {pathname: "/users", listState}}});
  };

  const handleOpenDeleteDialog = (id) => {
    const rows = dataGridProps.rows || [];
    const found = rows.find((user) => user.id === id);
    openDialog(id, found?.name || "");
  };

  const handleOpenTicketDialog = (user) => {
    setSelectedUser(user);
    setTicketDialogOpen(true);
  };

  const handleCloseTicketDialog = () => {
    setSelectedUser(null);
    setTicketDialogOpen(false);
  };

  const columns = [
    ...userColumns,
    ...(can(ACTIONS.DELETE, RESOURCES.USERS) || can(ACTIONS.WRITE, RESOURCES.USERS)
      ? [
          {
            field: "actions",
            headerName: "Actions",
            sortable: false,
            filterable: false,
            width: 120,
            renderCell: (params) => (
              <div style={{display: "flex", gap: "8px", alignItems: "center"}}>
                {can(ACTIONS.WRITE, RESOURCES.USERS) && (
                  <IconButton
                    aria-label="create-ticket"
                    color="primary"
                    size="small"
                    title="Crear ticket"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenTicketDialog(params.row);
                    }}
                  >
                    <FiMail />
                  </IconButton>
                )}
                {can(ACTIONS.DELETE, RESOURCES.USERS) && (
                  <IconButton
                    aria-label="delete"
                    color="error"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDeleteDialog(params.row.id);
                    }}
                  >
                    <FiTrash2 />
                  </IconButton>
                )}
              </div>
            )
          }
        ]
      : [])
  ];

  return (
    <>
      <Window
        title={"Usuarios"}
        topRightButtons={
          <ExportCsvButton columns={userColumns} filename="usuarios" fetchRows={() => fetchAllPages(list, filters)} />
        }
      >
        {/* Barra de filtros */}
        <SearchBar fields={userSearchFields} initialValues={filters} onSearch={applyFilters} onReset={resetFilters} />

        <ListView
          columns={columns}
          onRowClick={handleRowClick}
          dataGridProps={{
            ...dataGridProps
          }}
        />
      </Window>

      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="usuario"
        entityLabel={itemName}
      />

      <Dialog
        open={ticketDialogOpen}
        onClose={handleCloseTicketDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.colors.background
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.sidebar.background,
            color: theme.palette.windowScreen.title,
            fontWeight: "bold",
            pr: 6
          }}
        >
          Generar Ticket de Invitación
          <IconButton
            aria-label="Cerrar"
            onClick={handleCloseTicketDialog}
            sx={{position: "absolute", right: 8, top: 8, color: theme.palette.windowScreen.title}}
          >
            <FiX />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{pt: 4, pb: 4, color: theme.palette.text.primary}}>
          <Box sx={{mt: 3}}>
            <TicketForm
              prefilled={{
                email: selectedUser?.email,
                name: selectedUser?.name,
                surname: selectedUser?.surname
              }}
              onSuccess={handleCloseTicketDialog}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Users;
